import {
  isPendingFirstInstantConnectivityDocument,
  supportsInstantConnectivity,
} from 'support/helpers/relationships/relationships';
import { RelationshipV2 } from 'support/types';
import { StepStatus } from 'components/Display/Steps/Steps';

export type StepStage = 'configuration' | 'tasks' | 'testing' | 'goLive' | 'done' | 'terminated';

const STATUS_TO_STAGE: Record<RelationshipV2['status'], StepStage> = {
  PARTNER_APPROVAL_PENDING: 'configuration',
  PARTNER_ONBOARDING_PENDING: 'configuration',
  PARTNER_INTEGRATION_PENDING: 'configuration',
  PARTNER_PROCESS_PENDING: 'configuration',
  ISSUE_RESOLVER_PENDING: 'configuration',
  TASKS_PENDING: 'tasks',
  READY_FOR_TESTING: 'testing',
  HAS_TEST_MESSAGES: 'testing',
  IN_REVIEW: 'testing',
  GO_LIVE_DATE_PENDING: 'goLive',
  GO_LIVE_DATE_SCHEDULED: 'goLive',
  ACTIVE: 'goLive',
  LIVE: 'done',
  REJECTED: 'terminated',
  DEACTIVATED: 'terminated',
  BLOCKED: 'terminated',
};

export const getStepStage = (status: RelationshipV2['status']): StepStage => STATUS_TO_STAGE[status];

// Instant-connectivity relationships go ACTIVE (mapping to `goLive`) before any document has been
// exchanged, which would mark Configuration as completed. Until the first document arrives, the
// routing task is still pending, so keep the effective stage at `configuration`.
export const getCurrentStage = (relationship: RelationshipV2 | undefined): StepStage => {
  if (!relationship) {
    return 'configuration';
  }

  if (isPendingFirstInstantConnectivityDocument(relationship)) {
    return 'configuration';
  }

  // Instant-connectivity relationships that require testing sit at READY_FOR_TESTING while waiting for
  // the first routed document. There is nothing to test until it arrives, so keep Configuration (and
  // its routing task) open rather than jumping to Test Document Flow. Once a test transaction exists
  // the status advances to HAS_TEST_MESSAGES / IN_REVIEW and testing becomes current as normal.
  if (supportsInstantConnectivity(relationship) && relationship.status === 'READY_FOR_TESTING') {
    return 'configuration';
  }

  return getStepStage(relationship.status);
};

export const getStepStatus = (
  stepStage: StepStage,
  currentStage: StepStage,
  stageOrder: Array<StepStage>,
): StepStatus => {
  if (currentStage === 'terminated') {
    return 'disabled';
  }

  if (currentStage === 'done') {
    return 'completed';
  }

  const stepIndex = stageOrder.indexOf(stepStage);
  const currentIndex = stageOrder.indexOf(currentStage);

  if (currentIndex > stepIndex) {
    return 'completed';
  }

  if (currentIndex === stepIndex) {
    return 'current';
  }

  return 'upcoming';
};
